import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { inject, observer } from 'mobx-react'
import CategoryItem from './CategoryItem'
import { getTranslatedTextByKey, codeToLocale, isMobileApp, getLocaleStr } from 'utils/utils'
import { navigateToMenuSection } from 'utils/menu/menuUtils'
import { CONSTANTS, ORDER_TYPES } from 'utils/constants'
import { isEmpty } from 'lodash-es'
import type SectionID from 'types/SectionID'
import type ItemID from 'types/ItemID'
import type Title from 'types/Title'
import WithInView from 'HOCs/withInView'
import SectionTitleWithDividerBase from 'components/common/SectionTitleWithDivider'
import { StoreContext } from 'contexts/StoreContext'
import { FeatureFlagEnum } from 'types/constants.types'
import { useRouter } from 'next/router'
import { useStores } from 'hooks/useStores'

const Wrapper = styled.div`
	@media (max-width: 899px) {
		width: unset;
	}

	@media (max-width: 576px) {
		margin-top: unset;
		position: unset;
		top: unset;
		width: unset;
	}
`

const ContainerGrid = styled.div`
	gap: 20px;
	margin: 0 0 40px 0;
	display: grid;
	grid-template-columns: repeat(4, minmax(1px, 1fr));

	@media (max-width: 480px) {
		grid-template-columns: repeat(2, minmax(1px, 1fr));
	}

	@media (min-width: 481px) and (max-width: 1200px) {
		grid-template-columns: repeat(3, minmax(2px, 1fr));
	}
`

const SectionTitleWithDivider = styled(SectionTitleWithDividerBase)`
	margin-bottom: 25px;
`

interface CategoryItemType {
	sectionId: SectionID
	itemId: ItemID
	image: string
	title: Title
}

type SectionItemsType = Record<string, CategoryItemType[]>

interface BrowseCategoriesSectionProps {
	User: any
	Home: any
	Application: any
	Infra: any
}

const BrowseCategoriesSection = inject(
	'User',
	'Home',
	'Infra'
)(
	observer(({ User, Home, Infra }: BrowseCategoriesSectionProps) => {
		const [sectionItems, setSectionItems] = useState<SectionItemsType>({})
		const { locale: localData } = Home
		const router = useRouter()
		const { setStore } = useContext(StoreContext)
		const { cartStore, addressManagerV2Store } = useStores()

		let currentOrderType = ORDER_TYPES.DELIVERY as string

		if(addressManagerV2Store.newLocalizationFlowEnabled) {
			currentOrderType = (addressManagerV2Store.actualFlow?.orderTypeFlow?.orderType || ORDER_TYPES.DELIVERY) as string
		}

		else {
			currentOrderType =  CONSTANTS.DELIVERY_METHODS.DELIVERY === User.orderType ? ORDER_TYPES.DELIVERY : ORDER_TYPES.PICKUP
		}

		// @ts-ignore
		const locale = codeToLocale[User.preferredLanguage] || localData.msg
		const defaultCategory = {
			sectionId: '',
			itemId: '',
			image: '/images/allCategories.png',
			title: {
				[locale]: getTranslatedTextByKey('webviewFlow.browseCategories', 'Browse Categories'),
			},
		} as CategoryItemType

		const orderTypeSectionItems = sectionItems[currentOrderType]
		const browseCategories = isEmpty(orderTypeSectionItems) ? [] : [...orderTypeSectionItems, defaultCategory]

		useEffect(() => {
			if (addressManagerV2Store.isUserLocalized()) {
				fetchCategories()
			}
		}, [addressManagerV2Store.getFullAddressByOrderType(currentOrderType)])

		const fetchCategories = async () => {
			try {
				let chainOrStoreId = Infra?.appParams?.c

				if(addressManagerV2Store.newLocalizationFlowEnabled) {
					chainOrStoreId = addressManagerV2Store.actualFlow?.store?.id || Infra?.appParams?.c
				}
				else if(addressManagerV2Store.isUserLocalized()) {
					chainOrStoreId = addressManagerV2Store.getFullAddressByOrderType(currentOrderType).storeId
				}

				if (!chainOrStoreId) {
					return
				}

				const channelType = isMobileApp() ? CONSTANTS.CHANNEL_TYPE.APP : CONSTANTS.CHANNEL_TYPE.WEB
				const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
				const res = await Home.getBrowseCategories(chainOrStoreId, channelType, timezone)
				setSectionItems(res || {})
			} catch (err) {
				console.error(err)
			}
		}

		const handleCategoryClick = (categoryItem: CategoryItemType) =>
			navigateToMenuSection(categoryItem?.sectionId, getLocaleStr(categoryItem?.title, locale), setStore, cartStore, router, addressManagerV2Store)

		return (
			<WithInView
				callbackOnVisible={fetchCategories}
				triggerCallbackInstantly={!Infra?.hasFeatureFlag(FeatureFlagEnum.LAZY_LOAD_BROWSE_CATEGORIES)}
			>
				{!isEmpty(browseCategories) && (
					<Wrapper>
						<SectionTitleWithDivider
							title={getTranslatedTextByKey('webviewFlow.exploreTheMenu', 'Explore Our Menu')}
							testId="browse-categories-title"
						/>
						<ContainerGrid data-testid="browse-categories-container">
							{browseCategories?.map((categoryItem: CategoryItemType, index) => (
								<CategoryItem
									{...categoryItem}
									onClick={() => handleCategoryClick(categoryItem)}
									title={getLocaleStr(categoryItem?.title, locale)}
									allCategoriesItem={!!categoryItem.sectionId}
									key={categoryItem.itemId}
									testId={`browse-category-item-${index}`}
								/>
							))}
						</ContainerGrid>
					</Wrapper>
				)}
			</WithInView>
		)
	})
)

export default BrowseCategoriesSection
